import { RefObject, useCallback, useRef, useState } from 'react';

type TUseTruncationTooltip<T extends HTMLElement> = {
  ref: RefObject<T>;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
};

// 1px tolerance prevents false positives caused by sub-pixel rounding
const OVERFLOW_TOLERANCE = 1;

export const isElementTruncated = (element: HTMLElement | null): boolean => {
  if (!element) return false;
  return (
    element.scrollHeight - element.clientHeight > OVERFLOW_TOLERANCE ||
    element.scrollWidth - element.clientWidth > OVERFLOW_TOLERANCE
  );
};

/**
 * Controls a tooltip that is shown only when the referenced element's content
 * is visually truncated (e.g. by CSS line clamp or text-overflow: ellipsis).
 * Truncation is measured at hover time, so it stays correct after resizes.
 */
export const useTruncationTooltip = <
  T extends HTMLElement = HTMLElement,
>(): TUseTruncationTooltip<T> => {
  const ref = useRef<T>(null);
  const [open, setOpen] = useState(false);

  const onOpen = useCallback(() => {
    if (isElementTruncated(ref.current)) {
      setOpen(true);
    }
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  return { ref, open, onOpen, onClose };
};
